import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatterDispatch } from '@state/matter';
import { createId, FILETYPES, getFileDescriptions } from '@core/util/file';
import {
	MatterDocumentType,
	MortgageDocumentSubType,
} from '@models/matter-file';
import { ICON } from '@core/util/icon';
import {
	UploadDocumentModalComponent,
	UploadModalDocumentData,
} from 'src/app/private/sale/documents/uploaded/modal/modal.component';
import { MatDialog } from '@angular/material/dialog';
import { MortgageTag, MortgageDocumentTypeTag } from '@core/util/tags';
import { FileUpload } from '@state/file-upload/fileUpload.state';
import { TestHooks } from './sale-file-upload.hooks';
import { MatterUploadedFilesSelectors } from 'src/app/shared/state/matter/uploaded-files/matter-uploaded-files.selectors';
import { select } from '@ngxs/store';
import { ToastService } from 'src/app/ui/toast/toast.service';
import LogRocket from 'logrocket';

@Component({
	selector: 'app-sale-file-upload',
	templateUrl: './sale-file-upload.component.html',
	styleUrls: ['./sale-file-upload.component.scss'],
	standalone: false,
})
export class SaleFileUploadComponent implements OnInit {
	testHooks = TestHooks;
	public docId = createId();
	public fileDescriptions: string;
	public ICON = ICON;
	private defaultFileTypes: FILETYPES[] = [
		FILETYPES.JPG,
		FILETYPES.PNG,
		FILETYPES.PDF,
		FILETYPES.DOCX,
	];

	@Input() label: string;
	@Input() saleId: string;
	@Input() documentName: string;
	@Input() documentType: MatterDocumentType;
	@Input() type: 'default' | 'dropzone' | 'modal' = 'default';
	@Input() systemTags: string[] = [];
	@Input() contextId: string;
	@Input() set fileTypeList(list: FILETYPES[]) {
		const _list = list?.length > 0 ? list : this.defaultFileTypes;
		this.fileDescriptions = getFileDescriptions(_list);
	}
	@Input() runAiScraping: boolean;
	@Input() preventDuplicateUpload = false;
	files_ = select(MatterUploadedFilesSelectors.getAllUploadedFiles);

	private _builtSystemTags: string[] = [];

	@Output() success: EventEmitter<FileUpload> = new EventEmitter();
	@Output() failed: EventEmitter<void> = new EventEmitter();

	constructor(
		private saleDispatch: MatterDispatch,
		private matDialog: MatDialog,
		private toastService: ToastService,
	) {}

	ngOnInit(): void {
		if (!this.fileDescriptions) {
			this.fileDescriptions = getFileDescriptions(this.defaultFileTypes);
		}
		this._builtSystemTags = this.buildSystemTags(this.systemTags);
	}

	_fileSelected($event: File): void {
		if (
			this.preventDuplicateUpload &&
			this.hasFileUploaded(this.documentType)
		) {
			const errorMessage = 'File of this type has already been uploaded.';
			this.toastService.error(errorMessage);
			LogRocket.error(`Error SaleFileUploadComponent: ${errorMessage}`);
			return;
		}

		const documentName = this.documentName ? this.documentName : $event.name;
		this.saleDispatch.UploadMatterFile(
			this.docId,
			this.saleId,
			documentName,
			$event,
			this.documentType,
			this._builtSystemTags,
		);
	}

	hasFileUploaded(type: MatterDocumentType): boolean {
		return this.files_()?.some((file) => file.type === type);
	}

	_success($event: FileUpload): void {
		this.success.emit($event);
	}

	_failed($event: void): void {
		this.failed.emit($event);
	}

	upload(): void {
		// Build latest systemTags with current contextId
		const latestSystemTags = this.buildSystemTags(this.systemTags);

		const data: UploadModalDocumentData = {
			matterId: this.saleId,
			defaultFileType: this.documentType,
			lockFileType: !!this.documentType,
			systemTags: latestSystemTags,
			contextId: this.contextId,
			runAiScraping: this.runAiScraping,
		};
		this.matDialog.open(UploadDocumentModalComponent, {
			data,
			width: '80vw',
			maxWidth: '800px',
		});
	}

	private buildSystemTags(baseTags: string[] = []): string[] {
		const latestSystemTags = new Set(baseTags);

		if (this.contextId) {
			latestSystemTags.add(MortgageTag(this.contextId));

			if (this.documentType === MatterDocumentType.MORTGAGE) {
				latestSystemTags.add(
					MortgageDocumentTypeTag(
						MortgageDocumentSubType.MORTGAGE_INSTRUCTIONS,
					),
				);
			}
		}

		return Array.from(latestSystemTags);
	}
}
